import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ServingCommunitySection from '../../components/feature/ServingCommunitySection';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';
const PAGE_URL = `${SITE_URL}/life-insurance-indiana`;

const faqs = [
  {
    q: 'How much does life insurance cost in Indiana?',
    a: 'A healthy 35-year-old Indiana resident can get $500,000 of 20-year term life insurance for $25–$40/month. Rates depend on age, health, coverage amount, and term length. Indiana residents typically pay 8–12% below the national average due to the state\'s lower cost of living.',
  },
  {
    q: 'Do I need a medical exam to get life insurance in Indiana?',
    a: 'Not always. Many carriers offer no-exam life insurance for healthy applicants under 60. Coverage up to $1 million is available with just a health questionnaire. Applicants with certain health conditions may need a full exam.',
  },
  {
    q: 'Is life insurance regulated in Indiana?',
    a: 'Yes. All life insurance carriers operating in Indiana are regulated by the Indiana Department of Insurance (IDOI). Policies are also protected up to $300,000 by the Indiana Life & Health Insurance Guaranty Association.',
  },
  {
    q: 'What is the best type of life insurance for Indiana families?',
    a: 'For most middle-income Indiana families, term life insurance offers the best value — maximum coverage at the lowest cost. Whole life is better for those who want permanent coverage and cash value accumulation. We help you compare both.',
  },
  {
    q: 'How quickly can I get covered in Indiana?',
    a: 'Most Indiana residents can get approved and covered within 24–48 hours. Some no-exam policies offer same-day coverage. Call (317) 648-2983 or submit a quote request to get started today.',
  },
  {
    q: 'Are life insurance death benefits taxed in Indiana?',
    a: 'No. Indiana does not tax life insurance death benefits. Beneficiaries receive the full payout income-tax-free. Indiana also has no state estate tax, making life insurance an especially powerful wealth transfer tool.',
  },
];

const products = [
  {
    icon: 'ri-shield-check-fill',
    title: 'Term Life Insurance',
    desc: 'The most affordable coverage for Indiana families. Lock in low rates for 10, 20, or 30 years and protect your income, mortgage, and family.',
    from: 'From $15/mo',
    link: '/term-life',
    color: 'bg-[#1A3A52]',
  },
  {
    icon: 'ri-heart-pulse-fill',
    title: 'Whole Life Insurance',
    desc: 'Permanent coverage that never expires. Builds guaranteed cash value you can borrow against — a true financial asset for Indiana families.',
    from: 'Permanent Coverage',
    link: '/whole-life',
    color: 'bg-[#D97642]',
  },
  {
    icon: 'ri-hand-heart-fill',
    title: 'Final Expense Insurance',
    desc: 'Cover funeral and end-of-life costs so your family isn\'t burdened. No medical exam required. Guaranteed acceptance for ages 50–85.',
    from: 'From $20/mo',
    link: '/final-expense',
    color: 'bg-[#2D6A4F]',
  },
  {
    icon: 'ri-home-heart-fill',
    title: 'Mortgage Protection',
    desc: 'Keep your family in their home if you pass away. Coverage matches your mortgage balance and decreases as you pay it down.',
    from: 'Matches Your Mortgage',
    link: '/mortgage-protection',
    color: 'bg-[#1A3A52]',
  },
  {
    icon: 'ri-funds-fill',
    title: 'Annuities',
    desc: 'Guaranteed retirement income you can\'t outlive. Fixed and indexed annuities for Indiana residents planning a secure retirement.',
    from: 'Guaranteed Income',
    link: '/annuities',
    color: 'bg-[#D97642]',
  },
  {
    icon: 'ri-stethoscope-line',
    title: 'No Medical Exam Life Insurance',
    desc: 'Get covered fast without a doctor visit. Ideal for busy Indiana families who want protection without the hassle of a medical exam.',
    from: 'Same-Day Coverage',
    link: '/no-medical-exam',
    color: 'bg-[#2D6A4F]',
  },
];

const stats = [
  { value: '1 in 3', label: 'Indiana households are underinsured', icon: 'ri-home-line' },
  { value: '$500K', label: 'Coverage from ~$25/month for healthy adults', icon: 'ri-money-dollar-circle-line' },
  { value: '24 hrs', label: 'Average approval time for Indiana residents', icon: 'ri-time-line' },
  { value: '100%', label: 'Tax-free death benefit — no Indiana state tax', icon: 'ri-percent-line' },
];

const cities = [
  'Indianapolis', 'Carmel', 'Fishers', 'Noblesville', 'Greenwood',
  'Avon', 'Brownsburg', 'Westfield', 'Plainfield', 'Zionsville',
  'Anderson', 'Muncie', 'Bloomington', 'Terre Haute', 'Columbus',
];

const featuredCities = [
  { name: 'Carmel', slug: 'carmel', county: 'Hamilton County', population: '102,000+' },
  { name: 'Fishers', slug: 'fishers', county: 'Hamilton County', population: '100,000+' },
  { name: 'Noblesville', slug: 'noblesville', county: 'Hamilton County', population: '75,000+' },
  { name: 'Greenwood', slug: 'greenwood', county: 'Johnson County', population: '65,000+' },
  { name: 'Indianapolis', slug: 'indianapolis', county: 'Marion County', population: '880,000+' },
];

const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${SITE_URL}/life-insurance-indiana#service`,
  name: 'Life Insurance Indiana',
  description: 'Comprehensive life insurance solutions for Indiana residents. Term life, whole life, final expense, and no medical exam options. Serving all 92 counties.',
  provider: {
    '@id': `${SITE_URL}/#organization`
  },
  areaServed: {
    '@type': 'State',
    name: 'Indiana'
  },
  serviceType: 'Life Insurance',
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    reviewCount: '143',
    bestRating: '5',
    worstRating: '1'
  },
  review: [
    {
      '@type': 'Review',
      author: { '@type': 'Person', name: 'Karen Mitchell' },
      datePublished: '2024-12-03',
      reviewBody: 'Benjamin helped our whole family get covered — my husband and I both got $500K term policies for less than $60/month combined. He explained every option clearly and never pushed us toward anything we didn\'t need. Best insurance experience we\'ve had in Indiana.',
      reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' }
    },
    {
      '@type': 'Review',
      author: { '@type': 'Person', name: 'Robert Daniels' },
      datePublished: '2024-11-18',
      reviewBody: 'I\'d been putting off getting life insurance for years. Called Hoosier Life Insurance and had a policy in place within 48 hours. No medical exam, great rate, and Benjamin walked me through everything. Highly recommend to any Indiana family.',
      reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' }
    },
    {
      '@type': 'Review',
      author: { '@type': 'Person', name: 'Lisa Harrington' },
      datePublished: '2024-10-29',
      reviewBody: 'As a single mom in Indianapolis, I needed affordable coverage fast. Benjamin found me a $400K policy for $28/month. The process was completely online and took less than 20 minutes. I feel so much better knowing my kids are protected.',
      reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' }
    },
    {
      '@type': 'Review',
      author: { '@type': 'Person', name: 'Tom Becker' },
      datePublished: '2024-09-14',
      reviewBody: 'Compared quotes from four different agents across Indiana. Hoosier Life Insurance beat every single one. Got $750K of 20-year term coverage at age 44 for $62/month. Benjamin was honest, fast, and incredibly knowledgeable about Indiana-specific regulations.',
      reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' }
    },
    {
      '@type': 'Review',
      author: { '@type': 'Person', name: 'Angela Reyes' },
      datePublished: '2024-08-07',
      reviewBody: 'My husband and I just bought our first home in Noblesville and needed mortgage protection. Benjamin matched our coverage to our loan balance and explained exactly how it works. Approved in one day. Couldn\'t be happier with the service.',
      reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' }
    }
  ]
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}/life-insurance-indiana#webpage`,
  url: `${SITE_URL}/life-insurance-indiana`,
  name: 'Life Insurance Indiana | Affordable Coverage for Hoosier Families',
  description: 'Get affordable life insurance in Indiana. Compare term life, whole life, final expense, and no medical exam options. Serving all 92 counties.',
  inLanguage: 'en-US',
  datePublished: '2024-01-15T00:00:00-05:00',
  dateModified: new Date().toISOString(),
  isPartOf: {
    '@id': `${SITE_URL}/#website`
  },
  breadcrumb: {
    '@id': `${SITE_URL}/life-insurance-indiana#breadcrumb`
  }
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  '@id': `${SITE_URL}/life-insurance-indiana#breadcrumb`,
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: SITE_URL
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Life Insurance Indiana',
      item: `${SITE_URL}/life-insurance-indiana`
    }
  ]
};

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': ['InsuranceAgency', 'LocalBusiness'],
  '@id': `${SITE_URL}/life-insurance-indiana#localbusiness`,
  name: 'Hoosier Life Insurance — Indiana Statewide',
  url: `${SITE_URL}/life-insurance-indiana`,
  telephone: '+13176482983',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Indianapolis',
    addressLocality: 'Indianapolis',
    addressRegion: 'IN',
    postalCode: '46235',
    addressCountry: 'US',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 39.7684,
    longitude: -86.1581,
  },
  areaServed: {
    '@type': 'State',
    name: 'Indiana',
  },
  description: 'Independent life insurance agency serving all 92 counties of Indiana. Term life, whole life, final expense, mortgage protection, and annuities for Indiana families.',
  aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.9', reviewCount: '143', bestRating: '5' },
  openingHoursSpecification: [
    { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '09:00', closes: '17:00' },
    { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '08:00', closes: '13:00' },
  ],
  priceRange: '$$',
};
const speakableSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}/life-insurance-indiana#speakable`,
  speakable: {
    '@type': 'SpeakableSpecification',
    xpath: [
      '/html/body//*[@id="li-indiana-price-summary"]',
      '/html/body//*[@id="li-indiana-why-section"]',
    ],
  },
  url: `${SITE_URL}/life-insurance-indiana`,
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  '@id': `${SITE_URL}/life-insurance-indiana#faq`,
  mainEntity: [
    {
      '@type': 'Question',
      name: 'How much does life insurance cost in Indiana?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Life insurance costs in Indiana vary by age, health, and coverage type. A healthy 30-year-old pays $15-25/month for $250,000 in term coverage, while a 40-year-old pays $25-40/month. Whole life insurance costs $200-400/month for the same coverage. Indiana residents benefit from competitive rates due to the state\'s lower cost of living.'
      }
    },
    {
      '@type': 'Question',
      name: 'What type of life insurance is best for Indiana families?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Term life insurance is best for most Indiana families because it provides maximum coverage at the lowest cost during critical income-earning years. A 20-30 year term policy covers your mortgage, children\'s education, and income replacement affordably. Whole life insurance works better for estate planning and leaving a legacy.'
      }
    },
    {
      '@type': 'Question',
      name: 'Do I need life insurance if I have coverage through work?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, employer-provided life insurance is typically only 1-2x your salary, which isn\'t enough for most families. Financial experts recommend 10-12x your annual income. Plus, employer coverage ends when you leave your job. A personal policy ensures your family stays protected regardless of employment changes.'
      }
    },
    {
      '@type': 'Question',
      name: 'Can I get life insurance with pre-existing conditions in Indiana?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, Indiana residents with pre-existing conditions can get life insurance through simplified issue or guaranteed issue policies. Controlled diabetes, high blood pressure, and past cancer (5+ years in remission) often qualify for simplified issue. Guaranteed issue policies accept all health conditions but have lower coverage limits.'
      }
    },
    {
      '@type': 'Question',
      name: 'How much life insurance do I need in Indiana?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Most Indiana families need 10-12x their annual income in life insurance coverage. For example, if you earn $60,000/year, you need $600,000-$720,000 in coverage. This ensures your family can pay off the mortgage, cover living expenses for 10+ years, and fund children\'s education without financial hardship.'
      }
    },
    {
      '@type': 'Question',
      name: 'Is life insurance tax-free in Indiana?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, life insurance death benefits are 100% income tax-free in Indiana and federally. Beneficiaries receive the full payout without tax deductions. Cash value growth in whole life insurance is also tax-deferred, and policy loans are tax-free. Indiana has no state estate tax, making life insurance an efficient wealth transfer tool.'
      }
    }
  ]
};

export default function LifeInsuranceIndianaPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Life Insurance Indiana | Affordable Statewide';
    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', 'Life insurance in Indiana — affordable term life, whole life, final expense & mortgage protection for Indiana families. Local agent, free quotes. Call (317) 648-2983.');
    setMeta('keywords', 'life insurance Indiana, Indiana life insurance, affordable life insurance Indiana, term life insurance Indiana, Indiana insurance agent');
    setMeta('og:title', 'Life Insurance Indiana | Hoosier Life Insurance', true);
    setMeta('og:description', 'Affordable life insurance for Indiana families. Term life, whole life, final expense & more. Free quotes from a local Indiana agent.', true);
    setMeta('og:url', PAGE_URL, true);
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', PAGE_URL);
    setMeta('last-modified', new Date().toISOString());

    // Inject schema scripts
    const schemas = [serviceSchema, webPageSchema, breadcrumbSchema, faqSchema, localBusinessSchema, speakableSchema];
    schemas.forEach((schema, idx) => {
      const id = `schema-li-indiana-${idx}`;
      let el = document.getElementById(id) as HTMLScriptElement | null;
      if (!el) {
        el = document.createElement('script');
        el.id = id;
        el.type = 'application/ld+json';
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(schema);
    });

    return () => {
      [0, 1, 2, 3, 4, 5].forEach(idx => {
        const el = document.getElementById(`schema-li-indiana-${idx}`);
        if (el) el.remove();
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      <Header />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-[#1A3A52] border-b border-[#1A3A52]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-2 text-sm">
          <a href="/" className="flex items-center gap-1.5 text-white/70 hover:text-[#D97642] transition-colors">
            <i className="ri-home-4-line text-base"></i>
            <span>Home</span>
          </a>
          <i className="ri-arrow-right-s-line text-white/40"></i>
          <span className="text-[#D97642] font-semibold">Life Insurance Indiana</span>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-24 bg-gradient-to-br from-[#0F1F2E] via-[#1A3A52] to-[#0D2235] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/408baa3ec29ffa68e745306159d60c9e.jpg"
            alt="Indiana statewide life insurance coverage"
            title="Life Insurance in Indiana — Statewide Coverage for Every Hoosier Family"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1400}
            height={700}
            className="w-full h-full object-cover object-top opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F1F2E]/70 via-[#1A3A52]/50 to-[#0F1F2E]/80"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#D97642]/20 border border-[#D97642]/40 text-[#D97642] text-xs font-semibold uppercase tracking-widest rounded-full mb-6">
                <i className="ri-map-pin-line"></i>
                Serving All of Indiana
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Indiana Life Insurance —<br />
                <span className="text-[#D97642]">Independent Agent, All 92 Counties</span>
              </h1>
              <p className="text-lg sm:text-xl text-gray-300 mb-8 leading-relaxed">
                Affordable life insurance for Indiana families — from Indianapolis to Bloomington, Fort Wayne to Evansville. Local agent, honest advice, no pressure.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <a
                  href="/#quote"
                  className="inline-flex items-center justify-center px-8 py-4 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#C26638] transition-all shadow-lg whitespace-nowrap"
                  aria-label="Get a free life insurance quote in Indiana"
                >
                  <i className="ri-file-list-3-line mr-2 text-lg"></i>
                  Get a Free Quote
                </a>
                <a
                  href="tel:+13176482983"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white/10 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/20 transition-all whitespace-nowrap"
                  aria-label="Call Hoosier Life Insurance at 317-648-2983"
                >
                  <i className="ri-phone-line mr-2 text-lg"></i>
                  (317) 648-2983
                </a>
              </div>
              <div className="flex flex-wrap gap-5 text-sm text-white/70">
                <div className="flex items-center gap-2"><i className="ri-shield-check-line text-[#D97642]"></i> Indiana Licensed Agent</div>
                <div className="flex items-center gap-2"><i className="ri-star-fill text-[#D97642]"></i> 5-Star Rated</div>
                <div className="flex items-center gap-2"><i className="ri-time-line text-[#D97642]"></i> 24-Hr Approval</div>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
                <h3 className="text-white font-bold text-xl mb-6">Quick Quote Estimator</h3>
                <div className="space-y-4">
                  {[
                    { age: '25–34', coverage: '$500,000', term: '30-Year Term', price: '~$18/mo' },
                    { age: '35–44', coverage: '$500,000', term: '20-Year Term', price: '~$28/mo' },
                    { age: '45–54', coverage: '$500,000', term: '20-Year Term', price: '~$65/mo' },
                    { age: '55–64', coverage: '$250,000', term: '10-Year Term', price: '~$85/mo' },
                  ].map((row, i) => (
                    <div key={i} className="flex items-center justify-between bg-white/10 rounded-xl px-5 py-3">
                      <div>
                        <div className="text-white font-semibold text-sm">Age {row.age}</div>
                        <div className="text-white/60 text-xs">{row.coverage} · {row.term}</div>
                      </div>
                      <div className="text-[#D97642] font-black text-lg">{row.price}</div>
                    </div>
                  ))}
                </div>
                <p className="text-white/40 text-xs mt-4 text-center">Sample rates for healthy non-smokers. Actual rates vary.</p>
                <a
                  href="/#quote"
                  className="mt-5 w-full inline-flex items-center justify-center px-6 py-3 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#C26638] transition-all whitespace-nowrap"
                >
                  Get Your Exact Rate
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <div id="li-indiana-price-summary" className="bg-[#1A3A52] py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((s, i) => (
            <div key={i} className="text-center">
              <div className="w-10 h-10 flex items-center justify-center bg-[#D97642]/20 rounded-full mx-auto mb-3">
                <i className={`${s.icon} text-[#D97642] text-xl`}></i>
              </div>
              <div className="text-2xl sm:text-3xl font-black text-white mb-1">{s.value}</div>
              <div className="text-xs text-gray-400 leading-snug">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Why Indiana Families Need Life Insurance */}
      <section id="li-indiana-why-section" className="py-20 bg-[#F9F7F4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 bg-white text-[#1A3A52] text-xs font-semibold uppercase tracking-widest rounded-full border border-gray-200 mb-5">
                Why It Matters
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3A52] mb-6 leading-tight">
                Why Indiana Families Need Life Insurance Now
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Indiana's median household income is $61,000. The average mortgage balance is $185,000. Without life insurance, a family can lose everything in months. Here's what's at stake:
              </p>
              <div className="space-y-5">
                {[
                  { icon: 'ri-home-heart-line', title: 'Mortgage Protection', desc: 'The average Indiana mortgage takes 30 years to pay off. Life insurance ensures your family keeps the home.' },
                  { icon: 'ri-graduation-cap-line', title: 'Children\'s Education', desc: 'Indiana college costs average $25,000/year. A policy ensures your kids\' education is funded regardless.' },
                  { icon: 'ri-money-dollar-circle-line', title: 'Income Replacement', desc: 'Your family needs 10–12x your income to maintain their lifestyle. Life insurance delivers it instantly.' },
                  { icon: 'ri-file-shield-2-line', title: 'Debt Coverage', desc: 'Car loans, credit cards, and medical bills don\'t disappear. Life insurance prevents your family from inheriting your debt.' },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 items-start">
                    <div className="w-11 h-11 flex items-center justify-center bg-[#D97642]/10 rounded-xl flex-shrink-0">
                      <i className={`${item.icon} text-[#D97642] text-xl`}></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#1A3A52] mb-1">{item.title}</h4>
                      <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Happy%20Indiana%20family%20of%20four%20sitting%20together%20on%20front%20porch%20of%20suburban%20home%20warm%20golden%20afternoon%20light%20parents%20and%20two%20children%20smiling%20relaxed%20safe%20protected%20feeling%20professional%20lifestyle%20photography%20clean%20background&width=600&height=680&seq=li-indiana-family-001&orientation=portrait"
                alt="Indiana family protected by life insurance"
                title="Indiana Family Protected by Affordable Life Insurance from Hoosier Life Insurance"
                loading="lazy"
                decoding="async"
                width={600}
                height={680}
                className="rounded-2xl shadow-2xl w-full h-full object-cover object-top"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#D97642]/10 rounded-xl">
                    <i className="ri-shield-check-fill text-[#D97642] text-2xl"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 font-medium">Indiana Families Protected</div>
                    <div className="text-2xl font-black text-[#1A3A52]">500+</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="inline-block px-4 py-1.5 bg-[#F7F9FB] text-[#1A3A52] text-xs font-semibold uppercase tracking-widest rounded-full border border-gray-200 mb-4">
              Our Products
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3A52] mb-4">
              Life Insurance Products for Indiana Residents
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Every product is available statewide — from Indianapolis to Fort Wayne, Bloomington to South Bend.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
            {products.map((p, i) => (
              <a
                key={i}
                href={p.link}
                className="group bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:border-[#D97642]/30 transition-all"
              >
                <div className={`${p.color} p-6 flex items-center gap-4`}>
                  <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl">
                    <i className={`${p.icon} text-white text-2xl`}></i>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg leading-tight">{p.title}</h3>
                    <span className="text-white/70 text-xs font-medium">{p.from}</span>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">{p.desc}</p>
                  <div className="flex items-center gap-1.5 text-[#D97642] font-semibold text-sm group-hover:gap-3 transition-all">
                    Learn More <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Indiana-Specific Protections */}
      <section className="py-20 bg-[#F9F7F4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="inline-block px-4 py-1.5 bg-white text-[#1A3A52] text-xs font-semibold uppercase tracking-widest rounded-full border border-gray-200 mb-4">
              Indiana State Protections
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3A52] mb-4">
              How Indiana Law Protects Your Policy
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Indiana has some of the strongest consumer protections for life insurance policyholders in the country.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: 'ri-government-line', title: 'IDOI Regulation', desc: 'All carriers are licensed and regulated by the Indiana Department of Insurance. File complaints at in.gov/idoi.' },
              { icon: 'ri-shield-star-line', title: '$300K Guaranty Protection', desc: 'The Indiana Life & Health Insurance Guaranty Association protects your policy up to $300,000 if a carrier fails.' },
              { icon: 'ri-money-dollar-circle-line', title: 'No State Death Benefit Tax', desc: 'Indiana does not tax life insurance death benefits. Your beneficiaries receive 100% of the payout.' },
              { icon: 'ri-lock-2-line', title: 'Creditor Protection', desc: 'Indiana law shields life insurance cash value and death benefits from creditors, lawsuits, and bankruptcy proceedings.' },
              { icon: 'ri-calendar-check-line', title: '30-Day Free Look', desc: 'Indiana carriers offer a 30-day free look period. Cancel for any reason within 30 days for a full premium refund.' },
              { icon: 'ri-file-shield-2-line', title: 'No Estate Tax', desc: 'Indiana has no state estate tax, making life insurance an even more powerful wealth transfer tool for Indiana families.' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:border-[#D97642]/30 hover:shadow-md transition-all">
                <div className="w-11 h-11 flex items-center justify-center bg-[#D97642]/10 rounded-xl mb-4">
                  <i className={`${item.icon} text-[#D97642] text-xl`}></i>
                </div>
                <h4 className="font-bold text-[#1A3A52] mb-2">{item.title}</h4>
                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compare Products */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3A52] mb-4">
              Which Life Insurance Is Right for You?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A quick comparison to help Indiana residents choose the right coverage.
            </p>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#1A3A52] text-white">
                  <th className="px-6 py-4 text-left font-semibold">Feature</th>
                  <th className="px-6 py-4 text-center font-semibold">Term Life</th>
                  <th className="px-6 py-4 text-center font-semibold">Whole Life</th>
                  <th className="px-6 py-4 text-center font-semibold">Final Expense</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Coverage Duration', 'Fixed term (10–30 yrs)', 'Lifetime', 'Lifetime'],
                  ['Monthly Cost', '$ Lowest', '$$$ Higher', '$$ Moderate'],
                  ['Cash Value', '✗ None', '✓ Builds over time', '✓ Small amount'],
                  ['Medical Exam', 'Often not required', 'Sometimes required', '✗ Never required'],
                  ['Best For', 'Young families, mortgages', 'Estate planning, legacy', 'Seniors, burial costs'],
                  ['Coverage Amount', '$100K – $2M+', '$25K – $1M+', '$5K – $50K'],
                ].map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}>
                    <td className="px-6 py-4 font-semibold text-[#1A3A52]">{row[0]}</td>
                    <td className="px-6 py-4 text-center text-gray-600">{row[1]}</td>
                    <td className="px-6 py-4 text-center text-gray-600">{row[2]}</td>
                    <td className="px-6 py-4 text-center text-gray-600">{row[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-8 text-center">
            <p className="text-gray-500 text-sm mb-4">Not sure which is right for you? Our Indiana agents will help you decide — free, no pressure.</p>
            <a
              href="/wealth-strategy-call"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#C26638] transition-all shadow-md whitespace-nowrap"
            >
              <i className="ri-calendar-check-line text-lg"></i>
              Book a Free Strategy Call
            </a>
          </div>
        </div>
      </section>

      {/* Cities We Serve */}
      <section className="py-20 bg-[#F9F7F4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-white text-[#1A3A52] text-xs font-semibold uppercase tracking-widest rounded-full border border-gray-200 mb-4">
              Statewide Coverage
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3A52] mb-4">
              Life Insurance Across Indiana
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We serve families in every corner of Indiana — from the suburbs of Indianapolis to rural communities statewide.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 justify-center mb-10">
            {cities.map((city, i) => (
              <Link
                key={i}
                to={`/service-areas/${city.toLowerCase().replace(/\s+/g, '-')}`}
                className="px-5 py-2.5 bg-white border border-gray-200 rounded-full text-sm font-medium text-[#1A3A52] hover:border-[#D97642] hover:text-[#D97642] transition-all shadow-sm cursor-pointer"
              >
                {city}
              </Link>
            ))}
          </div>
          <div className="text-center">
            <Link to="/service-areas" className="inline-flex items-center gap-2 text-[#D97642] font-semibold hover:text-[#C26638] transition-colors">
              View All Service Areas <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Explore Local Coverage - Featured Cities */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="inline-block px-4 py-1.5 bg-[#F7F9FB] text-[#1A3A52] text-xs font-semibold uppercase tracking-widest rounded-full border border-gray-200 mb-4">
              Explore Local Coverage
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3A52] mb-4">
              Featured Indiana Cities
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Get localized life insurance quotes and coverage information for your specific Indiana community.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {featuredCities.map((city, i) => (
              <Link
                key={i}
                to={`/service-areas/${city.slug}`}
                className="group bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:border-[#D97642]/30 transition-all"
              >
                <div className="bg-gradient-to-br from-[#1A3A52] to-[#0D2235] p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#D97642]/20 rounded-xl">
                      <i className="ri-map-pin-2-fill text-[#D97642] text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-xl">{city.name}</h3>
                      <span className="text-white/70 text-xs">{city.county}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-white/60 text-sm">
                    <i className="ri-group-line"></i>
                    <span>{city.population} residents</span>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    Get personalized life insurance quotes for {city.name} residents. Compare rates from 20+ carriers and find coverage that fits your budget.
                  </p>
                  <div className="flex items-center gap-1.5 text-[#D97642] font-semibold text-sm group-hover:gap-3 transition-all">
                    View Local Rates <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </Link>
            ))}
            <Link
              to="/service-areas"
              className="group bg-[#F9F7F4] border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:border-[#D97642]/30 transition-all flex items-center justify-center"
            >
              <div className="text-center p-8">
                <div className="w-16 h-16 flex items-center justify-center bg-[#D97642]/10 rounded-full mx-auto mb-4">
                  <i className="ri-map-2-line text-[#D97642] text-3xl"></i>
                </div>
                <h3 className="text-[#1A3A52] font-bold text-xl mb-2">View All Cities</h3>
                <p className="text-gray-600 text-sm mb-4">Explore coverage in 27+ Indiana communities</p>
                <div className="flex items-center justify-center gap-1.5 text-[#D97642] font-semibold text-sm group-hover:gap-3 transition-all">
                  See All Service Areas <i className="ri-arrow-right-line"></i>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3A52] mb-4">
              Life Insurance Indiana — FAQ
            </h2>
            <p className="text-lg text-gray-600">Common questions from Indiana residents about life insurance.</p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-gray-100 rounded-xl overflow-hidden shadow-sm">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left bg-white hover:bg-[#F9F7F4] transition-colors cursor-pointer"
                >
                  <span className="font-semibold text-[#1A3A52] pr-4">{faq.q}</span>
                  <i className={`ri-arrow-down-s-line text-[#D97642] text-xl flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`}></i>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 bg-white">
                    <p className="text-gray-600 leading-relaxed text-sm">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#0F1F2E] via-[#1A3A52] to-[#0F1F2E]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Get Life Insurance in Indiana Today
          </h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
            Speak with a licensed Indiana agent. Free quotes, honest advice, no pressure. Serving all of Indiana from our Indianapolis office.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/#quote"
              className="inline-flex items-center justify-center px-8 py-4 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#C26638] transition-all shadow-lg whitespace-nowrap"
            >
              <i className="ri-file-list-3-line mr-2 text-lg"></i>
              Get a Free Quote
            </a>
            <a
              href="tel:+13176482983"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/20 transition-all whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2 text-lg"></i>
              Call (317) 648-2983
            </a>
          </div>
          <p className="mt-6 text-white/40 text-xs">Licensed Indiana Agent · Indianapolis, IN 46256 · Mon–Fri 9AM–5PM · Sat 8AM–1PM</p>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={143} productName="Life Insurance Indiana" />
      <ServingCommunitySection productName="Life Insurance" productSlug="life-insurance-indiana" />
      <Footer />
    </div>
  );
}
